import { useEffect, useRef, useState } from 'react';
import OpenSeadragon from 'openseadragon';

interface DeepZoomViewerProps {
  /** URL to the .dzi manifest. Tile paths are resolved relative. */
  dziUrl: string;
  /** Optional alt-text for accessibility (rendered as aria-label on the container). */
  alt?: string;
}

/**
 * OpenSeadragon-backed deep-zoom viewer for huge map images. Tiles are
 * fetched on-demand from a DZI tile pyramid, so the browser never has to
 * decode a multi-gigabyte image into memory.
 */
export function DeepZoomViewer({ dziUrl, alt }: DeepZoomViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<OpenSeadragon.Viewer | null>(null);
  const [zoomPct, setZoomPct] = useState<number>(100);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    setReady(false);
    const viewer = OpenSeadragon({
      element: containerRef.current,
      prefixUrl: 'https://cdn.jsdelivr.net/npm/openseadragon@4/build/openseadragon/images/',
      tileSources: dziUrl,
      // Hide OSD's default toolbar — we render our own UI to match gallery style
      showNavigationControl: false,
      showZoomControl: false,
      showHomeControl: false,
      showFullPageControl: false,
      showSequenceControl: false,
      showRotationControl: false,
      // UX tuning
      animationTime: 0.6,
      springStiffness: 10,
      blendTime: 0.1,
      constrainDuringPan: true,
      visibilityRatio: 1.0,
      minZoomImageRatio: 1.0,
      maxZoomPixelRatio: 8,
      // Inputs
      gestureSettingsMouse: {
        scrollToZoom: true,
        clickToZoom: false,
        dblClickToZoom: true,
        pinchToZoom: true,
        dragToPan: true,
      },
      gestureSettingsTouch: {
        scrollToZoom: false,
        clickToZoom: false,
        dblClickToZoom: true,
        pinchToZoom: true,
        dragToPan: true,
      },
      // Performance
      immediateRender: false,
      preserveImageSizeOnResize: true,
    });

    viewerRef.current = viewer;

    viewer.addHandler('open', () => setReady(true));
    viewer.addHandler('zoom', () => {
      const z = viewer.viewport.getZoom(true);
      // Map OSD zoom to "100% = fit" convention
      const homeZoom = viewer.viewport.getHomeZoom();
      const ratio = homeZoom > 0 ? z / homeZoom : 1;
      setZoomPct(Math.round(ratio * 100));
    });

    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [dziUrl]);

  function zoomIn() {
    const v = viewerRef.current;
    if (!v) return;
    v.viewport.zoomBy(1.6);
    v.viewport.applyConstraints();
  }

  function zoomOut() {
    const v = viewerRef.current;
    if (!v) return;
    v.viewport.zoomBy(1 / 1.6);
    v.viewport.applyConstraints();
  }

  function reset() {
    const v = viewerRef.current;
    if (!v) return;
    v.viewport.goHome();
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#fffdf2',
        borderRadius: 'var(--radius-sm)',
        touchAction: 'none',
      }}
      aria-label={alt}
      data-testid="deep-zoom-viewer"
    >
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

      {!ready ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(20, 16, 12, 0.92)',
            color: '#fffdf2',
            fontFamily: 'var(--font-body)',
            gap: 20,
            zIndex: 5,
          }}
        >
          <div style={{ position: 'relative', width: 140, height: 140 }}>
            <img src="/img/logo/grunk-empty.png" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', opacity: 0.85 }} />
            <img
              src="/img/logo/grunk-full.png"
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                animation: 'grunk-indeterminate 1.6s ease-in-out infinite',
              }}
            />
          </div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: '0.95rem' }}>Loading deep-zoom tiles…</div>
          <style>{`@keyframes grunk-indeterminate { 0%{clip-path:inset(95% 0 0 0)} 50%{clip-path:inset(20% 0 0 0)} 100%{clip-path:inset(95% 0 0 0)} }`}</style>
        </div>
      ) : null}

      {/* Zoom controls — same styling as ZoomableImage */}
      <div
        style={{
          position: 'absolute',
          bottom: 'var(--space-3)',
          right: 'var(--space-3)',
          display: 'flex',
          gap: 4,
          background: 'rgba(10, 8, 7, 0.78)',
          backdropFilter: 'blur(10px)',
          padding: '4px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(255, 253, 242, 0.12)',
          zIndex: 6,
        }}
      >
        <button type="button" onClick={zoomOut} aria-label="Zoom out" style={zoomBtnStyle}>−</button>
        <div
          style={{ padding: '4px 10px', color: '#fffdf2', fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem', minWidth: 56, textAlign: 'center', lineHeight: '20px' }}
        >
          {zoomPct}%
        </div>
        <button type="button" onClick={zoomIn} aria-label="Zoom in" style={zoomBtnStyle}>+</button>
        <button type="button" onClick={reset} aria-label="Reset zoom" style={zoomBtnStyle}>⌂</button>
      </div>

      {/* Hint */}
      {ready && zoomPct <= 105 ? (
        <div
          style={{
            position: 'absolute',
            top: 'var(--space-3)',
            left: 'var(--space-3)',
            color: 'rgba(10, 8, 7, 0.55)',
            background: 'rgba(255, 253, 242, 0.85)',
            backdropFilter: 'blur(8px)',
            padding: '4px 10px',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.72rem',
            fontFamily: 'ui-monospace, monospace',
            pointerEvents: 'none',
            zIndex: 6,
          }}
        >
          deep-zoom tiled · scroll / pinch / double-click to zoom · drag to pan
        </div>
      ) : null}
    </div>
  );
}

const zoomBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  background: 'transparent',
  color: '#fffdf2',
  border: 'none',
  fontSize: '1rem',
  cursor: 'pointer',
  borderRadius: 4,
  lineHeight: '20px',
  padding: 0,
};
